import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appApi } from '../lib/api';
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronUp, Tag, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

type AccessLevel =
  | { levelName: string; type: 'group'; cognitoGroupName: string }
  | { levelName: string; type: 'attribute'; attributeKey: string; attributeValue: string };

interface AppForm { name: string; accessLevels: AccessLevel[] }
const emptyForm: AppForm = { name: '', accessLevels: [] };

function newGroupLevel(): AccessLevel { return { levelName: '', type: 'group', cognitoGroupName: '' }; }
function newAttrLevel(): AccessLevel  { return { levelName: '', type: 'attribute', attributeKey: '', attributeValue: '' }; }

export default function Applications() {
  const queryClient = useQueryClient();

  const [showForm, setShowForm]   = useState(false);
  const [editApp, setEditApp]     = useState<any | null>(null);
  const [form, setForm]           = useState<AppForm>(emptyForm);
  const [formError, setFormError] = useState('');
  const [expanded, setExpanded]   = useState<Set<string>>(new Set());

  const { data: apps, isLoading } = useQuery({ queryKey: ['apps'], queryFn: appApi.list });

  const createMutation = useMutation({
    mutationFn: appApi.create,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['apps'] }); setShowForm(false); setForm(emptyForm); setFormError(''); },
    onError: (e: any) => setFormError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ appId, data }: { appId: string; data: any }) => appApi.update(appId, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['apps'] }); setEditApp(null); setShowForm(false); setForm(emptyForm); setFormError(''); },
    onError: (e: any) => setFormError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: appApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['apps'] }),
  });

  const openCreate = () => { setEditApp(null); setForm(emptyForm); setFormError(''); setShowForm(true); };
  const openEdit = (app: any) => {
    const levels: AccessLevel[] = (app.accessLevels || []).map((l: any) =>
      l.type === 'attribute'
        ? { levelName: l.levelName, type: 'attribute', attributeKey: l.attributeKey || '', attributeValue: l.attributeValue || '' }
        : { levelName: l.levelName, type: 'group', cognitoGroupName: l.cognitoGroupName || '' }
    );
    setEditApp(app); setForm({ name: app.name, accessLevels: levels }); setFormError(''); setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    for (const l of form.accessLevels) {
      if (!l.levelName) { setFormError('Todos los niveles deben tener un nombre.'); return; }
      if (l.type === 'group' && !l.cognitoGroupName) { setFormError('Los niveles de tipo Grupo deben tener un grupo de Cognito.'); return; }
      if (l.type === 'attribute' && !l.attributeKey) { setFormError('Los niveles de tipo Atributo deben tener una clave.'); return; }
    }
    setFormError('');
    if (editApp) updateMutation.mutate({ appId: editApp.appId, data: form });
    else createMutation.mutate(form);
  };

  const addGroupLevel = () => setForm({ ...form, accessLevels: [...form.accessLevels, newGroupLevel()] });
  const addAttrLevel  = () => setForm({ ...form, accessLevels: [...form.accessLevels, newAttrLevel()] });
  const removeLevel   = (i: number) => setForm({ ...form, accessLevels: form.accessLevels.filter((_, idx) => idx !== i) });
  const updateLevel   = (i: number, patch: Partial<Record<string, string>>) => {
    const levels = [...form.accessLevels] as any[];
    levels[i] = { ...levels[i], ...patch };
    setForm({ ...form, accessLevels: levels });
  };
  const switchType = (i: number, newType: 'group' | 'attribute') => {
    const levels = [...form.accessLevels] as any[];
    const cur = levels[i];
    levels[i] = newType === 'group'
      ? { levelName: cur.levelName, type: 'group', cognitoGroupName: '' }
      : { levelName: cur.levelName, type: 'attribute', attributeKey: '', attributeValue: '' };
    setForm({ ...form, accessLevels: levels });
  };
  const toggleExpand = (appId: string) => {
    const next = new Set(expanded);
    next.has(appId) ? next.delete(appId) : next.add(appId);
    setExpanded(next);
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-foreground">Aplicaciones</h1>
          <p className="mt-1 text-sm text-muted-foreground">Definición de aplicaciones y su mapeo a grupos de Cognito.</p>
        </div>
        <button onClick={openCreate} className={btnPrimary}>
          <Plus className="h-4 w-4 mr-2" />Nueva aplicación
        </button>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando...</p>
      ) : !(apps as any[])?.length ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center shadow-sm">
          <p className="text-muted-foreground text-sm">No hay aplicaciones definidas.</p>
          <button onClick={openCreate} className="mt-3 text-accent hover:underline text-sm">Crear la primera aplicación</button>
        </div>
      ) : (
        <div className="space-y-3">
          {(apps as any[]).map((app: any) => (
            <div key={app.appId} className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleExpand(app.appId)} className="text-muted-foreground hover:text-foreground transition-colors">
                    {expanded.has(app.appId) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{app.name}</h3>
                    <p className="text-xs text-muted-foreground">{app.accessLevels?.length || 0} nivel{(app.accessLevels?.length || 0) !== 1 ? 'es' : ''} de acceso</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(app)} title="Editar" className={iconBtn}><Pencil className="h-4 w-4" /></button>
                  <button
                    onClick={() => { if (confirm(`¿Eliminar la aplicación "${app.name}"?`)) deleteMutation.mutate(app.appId); }}
                    title="Eliminar"
                    className={cn(iconBtn, 'hover:text-destructive hover:bg-destructive/10')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {expanded.has(app.appId) && (
                <div className="border-t border-border px-5 py-4">
                  {!app.accessLevels?.length ? (
                    <p className="text-sm text-muted-foreground">Sin niveles de acceso.</p>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs font-medium text-muted-foreground uppercase">
                          <th className="pb-2 pr-6 w-8">Tipo</th>
                          <th className="pb-2 pr-8">Nivel</th>
                          <th className="pb-2">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {app.accessLevels.map((level: any, idx: number) => (
                          <tr key={idx}>
                            <td className="py-2 pr-6">
                              {level.type === 'attribute'
                                ? <Tag className="h-3.5 w-3.5 text-accent" />
                                : <Shield className="h-3.5 w-3.5 text-muted-foreground" />}
                            </td>
                            <td className="py-2 pr-8 font-medium text-foreground">{level.levelName}</td>
                            <td className="py-2 font-mono text-muted-foreground text-xs">
                              {level.type === 'attribute'
                                ? <><span className="text-foreground">{level.attributeKey}</span> = <span className="text-accent">{level.attributeValue}</span></>
                                : level.cognitoGroupName}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
          {/* Add app button at bottom of list */}
          <button
            onClick={openCreate}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-accent hover:text-accent transition-colors"
          >
            <Plus className="h-4 w-4" /> Nueva aplicación
          </button>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowForm(false)} />

          {/* Modal — flex col, max height, footer always visible */}
          <div className="relative bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">

            {/* Fixed header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <h3 className="text-base font-semibold text-foreground">
                {editApp ? `Editar: ${editApp.name}` : 'Nueva Aplicación'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {formError && (
                <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                  {formError}
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nombre de la aplicación *</label>
                <input
                  required
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  placeholder="ej. Parking, AlmorzApp"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">Niveles de Acceso</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={addGroupLevel} className="text-xs text-accent hover:underline flex items-center gap-1">
                      <Shield className="h-3 w-3" /> Grupo
                    </button>
                    <button type="button" onClick={addAttrLevel} className="text-xs text-accent hover:underline flex items-center gap-1">
                      <Tag className="h-3 w-3" /> Atributo
                    </button>
                  </div>
                </div>
                {form.accessLevels.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin niveles. Agregá un nivel de grupo o de atributo.</p>
                ) : (
                  <div className="space-y-3">
                    {form.accessLevels.map((level, i) => (
                      <div key={i} className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
                        {/* Row 1: nombre + tipo selector + eliminar */}
                        <div className="flex gap-2 items-center">
                          <input
                            placeholder="Nombre del nivel (ej. Usuario)"
                            value={level.levelName}
                            onChange={e => updateLevel(i, { levelName: e.target.value })}
                            className={cn(inputCls, 'flex-1')}
                          />
                          <select
                            value={level.type}
                            onChange={e => switchType(i, e.target.value as 'group' | 'attribute')}
                            className={cn(inputCls, 'w-32 shrink-0')}
                          >
                            <option value="group">Grupo</option>
                            <option value="attribute">Atributo</option>
                          </select>
                          <button type="button" onClick={() => removeLevel(i)} className="text-muted-foreground hover:text-destructive shrink-0 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        {/* Row 2: campos según tipo */}
                        {level.type === 'group' ? (
                          <input
                            placeholder="Nombre del grupo en Cognito"
                            value={level.cognitoGroupName}
                            onChange={e => updateLevel(i, { cognitoGroupName: e.target.value })}
                            className={cn(inputCls, 'font-mono text-xs')}
                          />
                        ) : (
                          <div className="flex gap-2">
                            <input
                              placeholder="Clave (ej. custom:tenantId)"
                              value={level.attributeKey}
                              onChange={e => updateLevel(i, { attributeKey: e.target.value })}
                              className={cn(inputCls, 'flex-1 font-mono text-xs')}
                            />
                            <input
                              placeholder="Valor"
                              value={level.attributeValue}
                              onChange={e => updateLevel(i, { attributeValue: e.target.value })}
                              className={cn(inputCls, 'flex-1 text-xs')}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed footer — always visible */}
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-border shrink-0">
              <button type="button" onClick={() => setShowForm(false)} className={btnSecondary}>Cancelar</button>
              <button
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
                className={btnPrimary}
              >
                {(createMutation.isPending || updateMutation.isPending)
                  ? 'Guardando...'
                  : editApp ? 'Guardar cambios' : 'Crear aplicación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = 'block w-full border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const iconBtn = 'p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';
const btnPrimary = 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50';
const btnSecondary = 'inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted transition-colors';
